import {
  Box,
  Button,
  Disclaimer,
  Div,
  lightTheme,
  MenulyticsReport,
  P,
  Pill,
} from '@vgrubs/components';
import {
  formatReportDay,
  formatReportMoment,
  formatReportPeriod,
  ReportPhase,
  reportPhase,
} from '../menuHealthFormat';
import { ProgressFill, ProgressTrack } from './MenuHealthReportRow.styled';

const openInNewTab = (url: string | null) =>
  url && window.open(url, '_blank', 'noopener,noreferrer');

const PHASE_PILL: Record<ReportPhase, { label: string; color: string }> = {
  generating: { label: 'Generating', color: lightTheme.blue },
  ready: { label: 'Ready', color: lightTheme.green },
  expired: { label: 'Expired', color: lightTheme.darkGray },
  failed: { label: 'Failed', color: lightTheme.primary },
};

type Props = {
  report: MenulyticsReport;
  /** Left out on a brand's own screen, where every row is that brand. */
  brandName?: string;
  onShare: (report: MenulyticsReport) => void;
  onRetry: (reportId: number) => void;
  retrying: boolean;
};

export const MenuHealthReportRow = ({
  report,
  brandName,
  onShare,
  onRetry,
  retrying,
}: Props) => {
  const phase = reportPhase(report);
  const pill = PHASE_PILL[phase];

  return (
    <Box $p="16px 20px" $pSM="16px" $mb="8">
      <Div $flex $between $alignCenter $gap="12px">
        <Div $minWidth="0">
          {brandName && (
            <P $m="0 0 2px" $bold $left>
              {brandName}
            </P>
          )}
          {/* Period and creation time on lines of their own, so a phone
              never breaks a date in half. */}
          <P $m="0" $left $bold={!brandName}>
            {formatReportPeriod(report)}
          </P>
          <Disclaimer $m="2px 0 0" $left $color={lightTheme.darkGray}>
            Created {formatReportMoment(report.createdAt)}
            {/* Reports cover every menu of the brand now; older ones were
                built from a single menu and say nothing here. */}
            {report.brandMenuId === null && ' · All menus'}
          </Disclaimer>
        </Div>
        <Div $flexNone>
          <Pill $background={pill.color}>
            <Disclaimer $m="0" $color={lightTheme.white} $uppercase>
              {pill.label}
            </Disclaimer>
          </Pill>
        </Div>
      </Div>

      {phase === 'generating' && (
        <Div $mt="12">
          <ProgressTrack>
            <ProgressFill $percent={report.progressPercent} />
          </ProgressTrack>
          <Disclaimer $m="6px 0 0" $left $color={lightTheme.darkGray}>
            Generating... {report.progressPercent}%
          </Disclaimer>
        </Div>
      )}

      {phase === 'ready' && (
        <Div $flex $between $alignCenter $gap="16px" $mt="12" $columnSM>
          {/* Left beside the button on a desktop, centered above it on a phone. */}
          <Div $minWidth="0">
            <Div $flex $gap="16px" $centerSM>
              {report.reportUrl && (
                <ReportLink onClick={() => openInNewTab(report.reportUrl)}>
                  View report
                </ReportLink>
              )}
              {report.pdfUrl && (
                <ReportLink onClick={() => openInNewTab(report.pdfUrl)}>
                  View PDF
                </ReportLink>
              )}
            </Div>
            <Disclaimer
              $m="4px 0 0"
              $left
              $centerSM
              $color={lightTheme.darkGray}
            >
              Valid until {formatReportDay(report.reportExpiresAt)}
              {report.lastSharedAt &&
                ` · Shared on ${formatReportMoment(report.lastSharedAt)}`}
            </Disclaimer>
          </Div>
          <Button
            $small
            $selected
            $w="160px"
            $wSM="100%"
            onClick={() => onShare(report)}
          >
            Share with owner
          </Button>
        </Div>
      )}

      {phase === 'expired' && (
        <Disclaimer $m="12px 0 0" $left $color={lightTheme.darkGray}>
          The links expired on {formatReportDay(report.reportExpiresAt)}.
          Generate a new report to share it.
        </Disclaimer>
      )}

      {phase === 'failed' && (
        <Div
          $flex
          $between
          $alignCenter
          $gap="12px"
          $mt="12"
          $columnSM
          $alignStartSM
        >
          <P $m="0" $left $color={lightTheme.primary}>
            {report.errorMessage || 'The report could not be generated.'}
          </P>
          <Button
            $small
            $wSM="100%"
            $loading={retrying}
            disabled={retrying}
            onClick={() => onRetry(report.id)}
          >
            Retry
          </Button>
        </Div>
      )}
    </Box>
  );
};

/** The red underlined text link the location screen uses for View Earning Plans. */
const ReportLink = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: string;
}) => (
  <P
    $m="0"
    $bold
    $underline
    $pointer
    $color={lightTheme.primary}
    onClick={onClick}
  >
    {children}
  </P>
);
